import { QueryKey, useQuery } from '@tanstack/react-query'
import { API_ENDPOINTS } from 'config/api'
import { isSupportedChain, SupportedChainId } from 'config/chains'
import { useMemo } from 'react'

import type { BackendQueryOptions } from './api'
import { fetchBackendJson } from './api'

type BlockMapQueryOptions = BackendQueryOptions<BlockMap | undefined, BackendBlockLookupItem[]>

export type BlocksQueryParams = {
  chainId?: number
  timestamps?: number[]
}

type NormalizedBlocksQueryParams = {
  chainId: SupportedChainId
  timestamps: number[]
}

export type BackendBlockLookupItem = {
  timestamp: number
  block: {
    id: string
    number: number
    timestamp: number
  } | null
}

export type BlockMap = Map<number, number>

export const sanitizeBlockTimestamps = (timestamps: number[] | undefined): number[] => {
  if (!timestamps?.length) {
    return []
  }

  const seen = new Set<number>()
  const sanitized: number[] = []

  for (const raw of timestamps) {
    if (!Number.isFinite(raw)) continue

    const timestamp = Math.floor(raw)
    if (seen.has(timestamp)) continue

    seen.add(timestamp)
    sanitized.push(timestamp)
  }

  return sanitized.sort((a, b) => a - b)
}

export const normalizeBlocksQueryParams = (params: BlocksQueryParams): NormalizedBlocksQueryParams | null => {
  const timestamps = sanitizeBlockTimestamps(params.timestamps)

  if (!isSupportedChain(params.chainId) || timestamps.length === 0) {
    return null
  }

  return {
    chainId: params.chainId,
    timestamps,
  }
}

export const blocksQueryKeys = {
  lookup: (params: NormalizedBlocksQueryParams) =>
    ['blocks', params.chainId, 'lookup', params.timestamps.join(',')] as const,
}

const buildBlocksSearchParams = (params: NormalizedBlocksQueryParams) => {
  const searchParams = new URLSearchParams({
    chainId: params.chainId.toString(),
    timestamps: params.timestamps.join(','),
    op: 'lte',
  })

  return searchParams.toString()
}

export const fetchBlocks = async (
  params: NormalizedBlocksQueryParams,
  signal?: AbortSignal
): Promise<BackendBlockLookupItem[]> => {
  const searchParams = buildBlocksSearchParams(params)

  return fetchBackendJson<BackendBlockLookupItem[]>(`${API_ENDPOINTS.blocks}?${searchParams}`, {
    method: 'GET',
    signal,
  })
}

export const toBlockMap = (data: BackendBlockLookupItem[] | undefined): BlockMap | undefined => {
  if (!data?.length) {
    return undefined
  }

  const results = new Map<number, number>()

  data.forEach((entry) => {
    if (entry.block) {
      results.set(entry.timestamp, entry.block.number)
    }
  })

  return results.size > 0 ? results : undefined
}

export const fetchBlockMap = async (params: BlocksQueryParams, signal?: AbortSignal): Promise<BlockMap> => {
  const normalizedParams = normalizeBlocksQueryParams(params)

  if (!normalizedParams) {
    return new Map<number, number>()
  }

  const data = await fetchBlocks(normalizedParams, signal)
  return toBlockMap(data) ?? new Map<number, number>()
}

export const useGetBlockMapQuery = (params: BlocksQueryParams, options?: BlockMapQueryOptions) => {
  const { chainId, timestamps } = params
  const normalizedParams = useMemo(() => normalizeBlocksQueryParams({ chainId, timestamps }), [chainId, timestamps])
  const {
    skip,
    enabled: optionEnabled,
    queryKey,
    retry = false,
    refetchOnWindowFocus = false,
    refetchOnReconnect = false,
    refetchInterval = false,
    staleTime = Infinity,
    gcTime = Infinity,
    ...queryOptions
  } = options ?? {}
  const enabled = (optionEnabled ?? true) && !skip && Boolean(normalizedParams)

  return useQuery<BackendBlockLookupItem[], Error, BlockMap | undefined>({
    queryKey: (queryKey ?? (normalizedParams ? blocksQueryKeys.lookup(normalizedParams) : ['blocks'])) as QueryKey,
    queryFn: ({ signal }) => {
      if (!normalizedParams) {
        throw new Error('Invalid blocks params')
      }

      return fetchBlocks(normalizedParams, signal)
    },
    enabled,
    retry,
    refetchOnWindowFocus,
    refetchOnReconnect,
    refetchInterval,
    staleTime,
    gcTime,
    select: toBlockMap,
    ...queryOptions,
  })
}
